// Pestaña «incidencias»: clasifica y agrupa por función los pedidos con incidencia de
// GET /admin/incidencias (como máximo 500, con los campos del pedido) y las funciones con
// incidencia de GET /admin/funciones. La API no guarda un tipo, solo el texto: el tipo se deduce
// del texto (usina-api: services/ticketing.js y services/migration.js) y la clase, de la fecha de
// la función en hora de España. La clave `trabajos` de la respuesta ya no se usa (tarea 1b).
import { inicioDiaMadrid } from '../../utils/hora';
import { normalizar } from './buscarFunciones';

export const LIMITE_API = 500;

/** Textos que asigna la API, reconocidos por su inicio. */
export const TIPOS = [
  { clave: 'discrepancia', prefijo: 'Discrepancia histórica', etiqueta: 'Discrepancia histórica de contadores' },
  { clave: 'reembolso-parcial', prefijo: 'Reembolso parcial externo', etiqueta: 'Reembolso parcial externo' },
  { clave: 'devolucion-pendiente', prefijo: 'Devolución pendiente', etiqueta: 'Devolución sin respuesta del proveedor' },
  { clave: 'devolucion-fallida', prefijo: 'El proveedor no completó la devolución', etiqueta: 'Devolución fallida' },
  { clave: 'cobro-sin-plazas', prefijo: 'Cobro sin plazas', etiqueta: 'Cobro sin plazas' },
  { clave: 'pago-sin-resolver', prefijo: 'Pago sin resolver', etiqueta: 'Pago sin resolver' },
  { clave: 'conciliacion', prefijo: 'Conciliación pendiente', etiqueta: 'Conciliación de pago pendiente' },
].map(t => ({ ...t, patron: normalizar(t.prefijo) }));
const OTRA = { clave: 'otra', etiqueta: 'Otra incidencia' };

export const tipoIncidencia = texto => TIPOS.find(t => normalizar(texto).startsWith(t.patron)) || OTRA;

const numero = valor => (valor === null || valor === undefined || valor === '' || !Number.isFinite(Number(valor)) ? undefined : Number(valor));

/**
 * «historico» para funciones anteriores a hoy (00:00 de Madrid) con la discrepancia de la migración
 * o, en pedidos migrados (`legacyId`), la conciliación pendiente que queda cuando el proveedor ya no
 * encuentra el pago antiguo. Todo lo demás, y cualquier caso sin fecha conocida, «accion»: una
 * discrepancia en una función de hoy o futura bloquea la venta actual y sí requiere revisión.
 */
export function claseIncidencia(tipo, fecha, ahora = Date.now(), migrado = false) {
  const f = numero(fecha);
  const antigua = f !== undefined && f < inicioDiaMadrid(ahora);
  return antigua && (tipo.clave === 'discrepancia' || (tipo.clave === 'conciliacion' && migrado)) ? 'historico' : 'accion';
}

// «Obra histórica» es el comodín de GET /admin/funciones cuando la obra ya no existe.
const tituloFuncion = f => (f?.titulo && f.titulo !== 'Obra histórica' ? f.titulo : '');

function totales(grupos) {
  return grupos.reduce((t, g) => ({ funciones: t.funciones + 1, pedidos: t.pedidos + g.pedidos.length, entradas: t.entradas + g.entradas, importe: t.importe + g.importe }),
    { funciones: 0, pedidos: 0, entradas: 0, importe: 0 });
}

/**
 * Devuelve `{ accion, historico, totales, limiteAlcanzado }`. Cada grupo es una función (o, si el
 * pedido no la tiene cargada, su título y fecha) dentro de una clase, con los tipos de incidencia,
 * la incidencia propia de la función y los pedidos, entradas e importe (céntimos) afectados. Una
 * misma función puede aparecer en las dos clases. Orden: de la más futura a la más antigua, y
 * primero las que no tienen fecha conocida.
 */
export function clasificarIncidencias(pedidos, funciones = [], ahora = Date.now()) {
  const lista = Array.isArray(pedidos) ? pedidos : [];
  const porId = new Map((funciones || []).map(f => [String(f._id), f]));
  const grupos = new Map();
  function grupo(clase, { funcion, funcionId, titulo, fecha }) {
    const clave = `${clase}:${funcion ? funcion._id : funcionId || `${titulo}|${fecha}`}`;
    if (!grupos.has(clave)) grupos.set(clave, { clave, clase, funcionId: funcion ? String(funcion._id) : '', funcion: funcion || null,
      titulo: tituloFuncion(funcion) || titulo || funcion?.titulo || 'Obra sin título', fecha: numero(funcion?.fecha) ?? fecha, tipos: new Map(), incidenciaFuncion: '', pedidos: [], entradas: 0, importe: 0 });
    return grupos.get(clave);
  }
  function anotar(g, tipo, texto, esPedido) {
    const t = g.tipos.get(tipo.clave) || { clave: tipo.clave, etiqueta: tipo.etiqueta, textos: [], pedidos: 0 };
    if (!t.textos.includes(texto)) t.textos.push(texto);
    if (esPedido) t.pedidos += 1;
    g.tipos.set(tipo.clave, t);
  }
  for (const o of lista) {
    if (!o?.incidencia) continue;
    const funcion = porId.get(String(o.funcionId)) || null;
    const fecha = numero(funcion?.fecha) ?? numero(o.fechaObra);
    const tipo = tipoIncidencia(o.incidencia);
    const g = grupo(claseIncidencia(tipo, fecha, ahora, !!o.legacyId), { funcion, funcionId: o.funcionId ? String(o.funcionId) : '', titulo: o.titulo || '', fecha });
    anotar(g, tipo, o.incidencia, true);
    const cantidad = numero(o.cantidad) || 0; const amount = numero(o.amount) || 0;
    g.pedidos.push({ _id: String(o._id), titulo: o.titulo || g.titulo, fecha: numero(o.fechaObra) ?? fecha, email: o.email || '', cantidad, amount,
      estado: o.estado || '', bloqueada: !!o.bloqueada, incidencia: o.incidencia, tipo: tipo.etiqueta });
    g.entradas += cantidad; g.importe += amount;
  }
  for (const funcion of funciones || []) {
    if (!funcion?.incidencia) continue;
    const tipo = tipoIncidencia(funcion.incidencia);
    const g = grupo(claseIncidencia(tipo, funcion.fecha, ahora), { funcion });
    g.incidenciaFuncion = funcion.incidencia;
    anotar(g, tipo, funcion.incidencia, false);
  }
  const orden = g => g.fecha ?? Infinity;
  const ordenados = [...grupos.values()].map(g => ({ ...g, tipos: [...g.tipos.values()] }))
    .sort((a, b) => (orden(a) === orden(b) ? 0 : orden(b) > orden(a) ? 1 : -1));
  const accion = ordenados.filter(g => g.clase === 'accion'); const historico = ordenados.filter(g => g.clase === 'historico');
  return { accion, historico, totales: { accion: totales(accion), historico: totales(historico) }, limiteAlcanzado: lista.length >= LIMITE_API };
}
